import { useMemo, useState, useEffect, useContext } from 'react'
import { useTheme } from '@mui/material/styles'
import { Link } from 'react-router-dom'

import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import { MenuItem, Box, IconButton, Tooltip, Button, TextField, Typography, Alert } from '@mui/material'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import KeyOffIcon from '@mui/icons-material/KeyOff'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'
import KeyIcon from '@mui/icons-material/Key'

import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { addApiKey, deleteApiKey, getApiKeys, setApiKeyStatus, toggleApiKeyClientFunctionAccess } from '../../providers/clients-api'

import DataExportDialog from '/components/data-export-dialog'
import ClickToCopy from '/components/click-to-copy'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'
import excelExport from './key-list-to-excel'
import { confirm } from '/components/confim-dialog'
import StyledPaper from '../paper.jsx'
import { createTableStyles } from '../table-styles'

const defaultApiAccess = {
  enabled: false,
  status: "disabled"
}

const APIKeyAdmin = () => {
  const theme = useTheme()
  const tableStyles = useMemo(() => createTableStyles(theme), [theme])

  const { apiSource, apiKey, setProcessing, selectedClientId } = useContext(AppContext)

  const [loading, setLoading] = useState(true)
  const [keys, setKeys] = useState([])
  const [apiAccess, setApiAccess] = useState(defaultApiAccess)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)
  const [keyName, setKeyName] = useState("")
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  useEffect(() => {
    getKeys()
  }, [selectedClientId])

  const getKeys = async () => {
    setLoading(true)
    const result = await getApiKeys({ apiSource, apiKey })

    if (result.ok) {
      setKeys(result.keys || [])
      setApiAccess(result.apiAccess || defaultApiAccess)
    }
    setLoading(false)
  }

  const addKey = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Adding new API key', variant: 'light' })
    const result = await addApiKey({ apiSource, apiKey, keyName })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'API Key added', variant: 'success' })
      setKeys(result.keys)
      setKeyName("")
    } else {
      showActionError(result.error, 'Error adding API key')
    }
  }

  const deleteKey = async keyId => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this key rather than temporarily disable it?'
    })

    if (confirmed) {
      setProcessing(true)
      enqueueSnackbar({ message: 'Removing API Key', variant: 'light' })
      const result = await deleteApiKey({ apiSource, apiKey, keyId })
      setProcessing(false)

      if (result.ok) {
        enqueueSnackbar({ message: 'API Key removed', variant: 'success' })
        setKeys(result.keys)
      } else {
        showActionError(result.error, 'Error removing API key')
      }
    }
  }

  const toggleKeyEnabled = async keyId => {
    const keyRecord = keys.find(u => u.user_id === keyId)
    const status = 1 - keyRecord.user_enabled

    setProcessing(true)
    enqueueSnackbar({ message: 'Setting key status', variant: 'light' })
    const result = await setApiKeyStatus({ apiSource, apiKey, keyId, status })
    setProcessing(false)

    if (result.ok) {
      enqueueSnackbar({ message: 'Key status saved', variant: 'success' })
      setKeys(result.keys)
    } else {
      showActionError(result.error, 'Error setting API key status')
    }
  }

  const toggleClientFunctionAccess = async keyId => {
    const result = await toggleApiKeyClientFunctionAccess({ apiSource, apiKey, keyId })
    if (result.ok) {
      setKeys(result.keys)
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'user_id',
      header: 'Key ID',
      filterVariant: 'autocomplete',
      size: 80,
    },
    {
      accessorKey: 'username',
      header: 'Key name',
      filterVariant: 'autocomplete',
      size: 200,
      Cell: ({ cell }) => <ClickToCopy source={cell.getValue()} successMessage="Key name copied">{cell.getValue()}</ClickToCopy>,
    },
    {
      accessorKey: 'user_enabled',
      header: 'Enabled',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'allow_access_to_client_functions',
      header: 'Client Function Access',
      size: 100,
      filterVariant: 'select',
      filterFn: 'equals',
      filterSelectOptions: [
        { label: 'Yes', value: 1 },
        { label: 'No', value: 0 },
      ],
      Cell: ({ cell }) => cell.getValue() ? "yes" : "no"
    },
    {
      accessorKey: 'key',
      header: 'API Key',
      filterVariant: 'autocomplete',
      size: 200,
      Cell: ({ cell }) => <ClickToCopy source={cell.getValue()} successMessage="API key copied">{cell.getValue()}</ClickToCopy>,
    },
  ], [])

  const table = useMaterialReactTable({
    columns,
    data: keys,
    state: {
      isLoading: loading
    },
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: originalRow => originalRow.user_id,
    ...tableStyles,
    renderRowActionMenuItems: ({ closeMenu, row }) => getActions(toggleKeyEnabled, toggleClientFunctionAccess, deleteKey, closeMenu, row),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title="Export"><IconButton onClick={() => setDataExportDialogOpen(true)}><SaveAltIcon /></IconButton></Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row items-start gap-5'>
        <TextField value={keyName} onChange={e => setKeyName(e.target.value)} label="new key name" slotProps={{ inputLabel: { shrink: true } }} />
        <Button startIcon={<KeyIcon className='mr-2' />} onClick={addKey} disabled={keyName.length < 3} variant='contained'>Add Api Key</Button>
      </div>
    )
  })
  const apiAccessEnabled = apiAccess.enabled === true
  const tableHeight = 'calc(100vh - 185px)'

  return (
    <>
      {!loading && !apiAccessEnabled ? (
        <StyledPaper>
          <div className='flex flex-col gap-4 p-5 max-w-[900px]'>
            <Typography variant='h5'>API access is currently disabled</Typography>
            <Alert severity='warning'>
              API access is disabled for this account. Please contact <Link to='/support/history' className='link'>support</Link> if API access is required.
            </Alert>
          </div>
        </StyledPaper>
      ) : (
        <>
          <Box sx={{ height: tableHeight, width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <StyledPaper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} height="100vh">
              <MaterialReactTable table={table} />
            </StyledPaper>
          </Box>
        </>
      )}

      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName="api_keys" data={keys} excelExport={excelExport} />
      <ActionErrorDialog title="API key action failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default APIKeyAdmin

const getActions = (toggleKeyEnabled, toggleClientFunctionAccess, deleteKey, closeMenu, row) => {
  const actions = [
    <MenuItem key="toggle" onClick={() => { toggleKeyEnabled(row.original.user_id); closeMenu() }}><KeyIcon className='mr-2' />{row.original.user_enabled === 1 ? "Disable" : "Enable"} Key</MenuItem>,
    <MenuItem key="admin" onClick={() => { toggleClientFunctionAccess(row.original.user_id); closeMenu() }}><SupervisorAccountIcon className='mr-2' />{row.original.allow_access_to_client_functions === 1 ? "Disable" : "Enable"} Client Function Access for Key</MenuItem>,
    <MenuItem key="delete" onClick={() => { deleteKey(row.original.user_id); closeMenu() }}><KeyOffIcon className='mr-2' /> Delete Key</MenuItem>,
  ]

  return actions
}
